import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService, TreeNode} from 'primeng/api';
import {TeVariableTypeEnum} from '../../../enum/te-variable-type.enum';
import {MvsCrudService} from "../../../../../core/shared/service/crud/mvs-crud.service";
import {firstValueFrom, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {TeVariableComponentBase} from '../../../logic/te-variable-component-base';
import {TeContentProviderVariableDto} from "../../../model/dto/api/te.content-provider-variable.dto";
import {TeVariableProviderService} from "../../../service/api/interfaces/te-variable-provider.service";
import {TeTemplateVariableDto} from "../../../model/dto/entity/te-template-variable.dto";
import {ObjectTypeService} from "../../../../../core/cc/service/api/object-type.service";
import {ObjectTypeDto} from "../../../../../core/cc/model/dto/entity/object-type.dto";
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {MetaService} from "../../../../../core/shared/meta/service/meta.service";
import {MvsMessageService} from "../../../../../core/shared/service/message/mvs-message.service";
import {MvsUiObjectService} from "../../../../../core/shared/object/service/mvs-ui-object.service";
import {ObjectRequestList} from "../../../../../core/shared/dto/object/request/object-request-list";
import {Sorting} from "../../../../../core/shared/misc/sorting";
import {DtoList} from "../../../../../core/shared/dto/dto.list";
import {DtoImportObjectContext} from "../../../../../core/shared/dto/dto.import.object.context";
import {MvsCrudModeEnum} from "../../../../../core/shared/service/crud/mvs-crud-mode.enum";

import {MetaDataEntityDto} from "../../../../../core/shared/meta/dto/meta-data-entity.dto";
import {DataTypeHelper} from "../../../../../core/shared/helper/data-type.helper";
import {IconClassHelper} from "../../../../../core/shared/helper/icon-class-helper";
import {Popover} from "primeng/popover";
import {TeEditorCommunicationService} from '../../../service/te-editor-communication.service';
import {TeCopyPopoverComponent, TeCopyAttrData} from '../te-copy-popover/te-copy-popover.component';
import {TeTemplateVariableService} from "../../../service/api/te-template-variable.service";

@Component({
    selector: "mvs-te-variables",
    templateUrl: "./te-variables.component.html",
    styleUrls: ["./te-variables.component.scss"],
    standalone: false
})
export class TeVariablesComponent
    extends TeVariableComponentBase
    implements OnInit, OnChanges, OnDestroy {

    busy: boolean; // indicator whether the component is busy
    initialized: boolean; // indicator whether the component was initialized
    // @Input() height: number = 300;

    // When add button is active, then the user is able to add variable.
    @Input() isAddButtonShown?: boolean = true;
    // When title is active, then the title will be shown on the header.
    @Input() isTitleShown?: boolean = true;
    // we can pass the tree width for required width.
    @Input() treeWidth?: boolean = false;
    // When drag mode is active, then the user is able to drag one variable into the editor.
    @Input() dragMode?: boolean = true;
    /**
     * When select mode is active, then the user is able to select one variable. Whenever
     * a variable is selected then the event onVariableSelect will be emitted.
     */
    @Input() selectMode?: "single" | "multiple" | "checkbox" = null;

    @Output() onVariableSelect: EventEmitter<any> = new EventEmitter<any>();
    @Output() onVariableRenamed: EventEmitter<{ oldAlias: string, newAlias: string }> = new EventEmitter();
    @Output() onTableInsert: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild("simpleVariable", {static: false}) simpleVariablePanel: Popover;
    @ViewChild("objectVariable", {static: false}) objectVariablePanel: Popover;
    @ViewChild('copyPopover', {static: false}) copyPopoverRef: TeCopyPopoverComponent;
    objectTypeService: ObjectTypeService;
    objectTypes: ObjectTypeDto[];
    //nodes: TreeNode[];

    teVariableTypeEnum: typeof TeVariableTypeEnum = TeVariableTypeEnum;

    dataTypes = [
        {name: 'string'}, {name: 'number'}
    ]
    variableName: string;
    variableAlias: string;

    selectedVariableType: { name: string };

    selectedObjectType: ObjectTypeDto;
    variables: TreeNode[] = [];
    selectionNode: TreeNode;
    configurationItems: MenuItem[] | undefined;
    visible: boolean;
    variableToggle: boolean;
    displayTechnicalNames: boolean = false;
    contextMenuItems: MenuItem[] = [];
    selectedContextNode: TreeNode;

    // Deep search properties
    searchTerm: string = '';
    searchResultTree: TreeNode[] = [];
    private searchSubject: Subject<string> = new Subject<string>();

    constructor(
        protected coreService: MvsCoreService,
        protected metaService: MetaService,
        protected messageService: MvsMessageService,
        protected toast: MessageService,
        protected objectService: MvsUiObjectService,
        protected confirmationService: ConfirmationService,
        private editorCommunicationService: TeEditorCommunicationService,
        private templateVariableService: TeTemplateVariableService) {
        super();
    }

    ngOnInit(): void {
        this.initComponent();
        this.refreshComponent();

        // Set up debounced search
        this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe(term => {
            this.performDeepSearch(term);
        });
    }

    /**
     * Initialize Component.
     */
    initComponent() {

        this.variableProviderService = <MvsCrudService & TeVariableProviderService>this.coreService.getCrudService(this.alias);
        this.objectTypeService = <ObjectTypeService>(this.coreService.getCrudService("cc.ObjectType"));

    }

    /**
     * Refresh Component.
     */
    refreshComponent() {
        this.refreshVariables();

        this.initialized = true;
    }


    /**
     * Lazy-load object types only when the create-variable dialog opens.
     * Uses alias as display label to avoid flooding MetaService with per-type API calls.
     */
    getAllObjectTypes() {
        if (this.objectTypes && this.objectTypes.length > 0) {
            return; // Already loaded
        }
        this.objectTypeService.list(
            new ObjectRequestList(false, [], [new Sorting("alias", true)])
        ).subscribe((res: DtoList<ObjectTypeDto>) => {
            this.objectTypes = res.entries;

            // FIX: Use name instead of alias for display — shows "Customer" instead of "cr.Customer"
            for (const objectType of this.objectTypes) {
                if (!objectType['displayLabel']) {
                    objectType['displayLabel'] = objectType.name;
                }
            }

            this.objectTypes.sort((a, b) => (a['displayLabel'] || a.name).localeCompare(b['displayLabel'] || b.name));
        });
    }

    /**
     * Retrieve variables.
     */
    refreshVariables() {
        this.busy = true;
        this.variableProviderService.listVariables(this.contentProviderId).subscribe(value => {
            this.variables = value.map(variable => this.createRootNode(variable, ''));
            this.busy = false;
        });
    }

    /**
     * Create new variable.
     * @param type
     * @param objectType
     */
    createVariable(type: number, objectType: { name: string } & ObjectTypeDto | any) {
        this.visible = false;
        this.busy = true;
        const data: TeTemplateVariableDto = new TeTemplateVariableDto();
        data.name = this.variableName;
        data.type = type;
        data.multiple = false;
        data.optional = true;
        data['alias'] = this.variableAlias;

        data[`${this.attributeName}DtoId`] = this.contentProviderId;


        if (type == TeVariableTypeEnum.object) {
            data.objectTypeDtoId = objectType.id ? objectType.id : null;
            data.objectTypeDtoName = objectType.name ? objectType.name : null;
        }

        this.variableProviderService.create(data).subscribe(response => {

            this.busy = false;
            this.messageService.showSuccessMessage("Vorlage", "Variable erstellt");

            // Reset all form fields after successful creation
            this.variableName = '';
            this.variableAlias = '';
            this.selectedVariableType = null;
            this.selectedObjectType = null;
            this.visible = false;

            this.refreshVariables();

        }, error => {

            this.busy = false;
            this.messageService.showErrorMessage("Vorlage", "Ein Fehler ist aufgetreten.");
        });
    }

    handleEditVariableName(id: number, currentAlias: string) {
        this.objectService.openObjectViaDialog(
            new DtoImportObjectContext([]),
            this.alias,
            id,
            null,
            false,
            false,
            () => {
                // After edit dialog closes, check if alias changed and emit rename event
                this.variableProviderService.listVariables(this.contentProviderId).subscribe(variables => {
                    const updatedVariable = variables.find(v => v.id === id);
                    if (updatedVariable && updatedVariable.alias !== currentAlias) {
                        this.onVariableRenamed.emit({
                            oldAlias: currentAlias,
                            newAlias: updatedVariable.alias
                        });
                    }
                    this.variables = variables.map(variable => this.createRootNode(variable, ''));
                });
            },
            MvsCrudModeEnum.create
        );
    }

    handleDeleteVariableName(id: number): void {
        this.confirmationService.confirm({
            message: 'Möchten Sie diese Variable wirklich löschen?',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.deleteVariableName(id);
            },
            reject: () => {
            },
        });
    }

    deleteVariableName(id: number) {
        this.busy = true;
        this.variableProviderService.delete(id).subscribe(response => {

            this.busy = false;
            this.messageService.showSuccessMessage("Vorlage", "Variable gelöscht");

            this.refreshVariables();

        }, error => {

            this.busy = false;
            this.messageService.showErrorMessage("Vorlage", "Fehler beim Löschen der Variable.");
        });
    }

    createRootNode(nodeItem: TeContentProviderVariableDto, iconType: string): TreeNode {

        let expandedIcon: string;
        let collapsedIcon: string;
        let isLeaf: boolean = false;
        let nodeType: string;

        // FIX: The API returns variableType as numeric ordinals (0=simple, 1=object, 2=system)
        // but the DTO declares it as string union ('simple'|'object'|'system').
        // Cast to `any` to allow comparing against both string and numeric enum values.
        // This ensures correct template assignment (simple-root, object-root, system-root).
        const varType: any = nodeItem.variableType;
        if (varType === 'simple' || varType === TeVariableTypeEnum.simple) {
            expandedIcon = 'fa-regular fa-font text-green-600';
            collapsedIcon = 'fa-regular fa-font text-green-600';
            isLeaf = true;
            nodeType = 'simple-root';
        } else if (varType === 'object' || varType === TeVariableTypeEnum.object) {
            expandedIcon = 'fa-regular fa-cube text-blue-500';
            collapsedIcon = 'fa-regular fa-cube text-blue-500';
            nodeType = 'object-root';
        } else if (varType === 'system' || varType === TeVariableTypeEnum.system) {
            expandedIcon = 'fa-regular fa-gear text-purple-500';
            collapsedIcon = 'fa-regular fa-gear text-purple-500';
            nodeType = 'system-root';
        }


        let node: TreeNode = {
            label: nodeItem.name,
            data: nodeItem,
            expandedIcon: expandedIcon,
            collapsedIcon: collapsedIcon,
            leaf: isLeaf,
            children: isLeaf ? undefined : [{}],
            type: nodeType
        };

        return node;
    }


    createNode(nodeItem: any, iconType: string): TreeNode {

        // set label for node — prefer human-readable label from backend, fall back to typeName/alias/name
        const label = nodeItem.label || nodeItem.typeName || nodeItem.joinObjectTypeAlias || nodeItem.name;

        let icon;

        // Backend may send joinRelationEnum as string ('manyToOne') or numeric ordinal (3)
        // Enum ordinals: 0=none, 1=manyToMany, 2=oneToOne, 3=manyToOne, 4=oneToMany
        const joinRelation = nodeItem.joinRelationEnum != null ? String(nodeItem.joinRelationEnum) : null;

        switch (iconType) {
            case 'joins':
                switch (joinRelation) {
                    case 'oneToOne':
                    case 'manyToOne':
                    case '2': // oneToOne ordinal
                    case '3': // manyToOne ordinal
                        icon = 'fa-regular fa-link text-cyan-600';
                        break;
                    case 'oneToMany':
                    case '4': // oneToMany ordinal
                        icon = 'fa-regular fa-layer-group text-orange-500';
                        break;
                    default:
                        icon = 'fa-regular fa-link text-cyan-600';
                        break;
                }
                break;
            case 'generic':
                icon = 'fa-regular fa-layer-group text-orange-500';
                break;
            default:
                icon = 'fa-regular fa-folder';
                break;
        }
        const expandedIcon: string = icon == 'fa-regular fa-folder' ? 'fa-regular fa-folder-open' : icon;

        // Determine node type based on join relation
        let nodeType = 'join'; // default for joins
        if (iconType === 'joins') {
            switch (joinRelation) {
                case 'oneToMany':
                case '4': // oneToMany ordinal
                    nodeType = 'collection';
                    break;
                case 'manyToOne':
                case 'oneToOne':
                case '2': // oneToOne ordinal
                case '3': // manyToOne ordinal
                    nodeType = 'join';
                    break;
            }
        } else if (iconType === 'generic') {
            nodeType = 'collection';
        }

        let node: TreeNode = {
            label: label,
            data: nodeItem,
            expandedIcon: expandedIcon,
            collapsedIcon: icon,
            leaf: true,
            children: [{}],
            type: nodeType
        };

        return node;
    }


    nodeExpandOnMetaData(node: TreeNode, res: MetaDataEntityDto) {

        // declaring variables
        let childrens: any = [];
        let genericType: any;
        let joins: any;

        const attributes = res.attributes;

        // assigning node attributes
        const children = attributes.map((element: any) => {
            // returns attribute object
            return this.setAttributes(element);
        });
        // assigning node sub joins
        joins = res.joins.map((items: any) => this.createNode(items, 'joins'));

        // assigning node generic types if any
        if (res.genericType) {
            genericType = res.genericTypes.map((items: any) => this.createNode(items, 'generic'));
        }

        // merging/concating all attributes joins and generic types
        childrens = res.genericType ? childrens.concat(genericType, children, joins) : childrens.concat(children, joins);


        // assigning childrens to node
        node.children = childrens;
    }

    /**
     * Expand Nodes.
     * @param event
     */
    nodeExpand(event: any) {

        // If this is a search-expanded node being re-expanded, do a full load with highlight preservation
        if (event.node.data?.searchExpanded) {
            this.expandNodeFully(event.node);
            return;
        }

        let attributes: any;

        // FIX: Same numeric/string variableType mismatch — cast to any for dual comparison
        const expandVarType: any = event.node.data?.variableType;
        if ((expandVarType === 'system' || expandVarType === TeVariableTypeEnum.system) && !event.node.data.objectTypeDtoId) {

            this.busy = true;

            const systemVariable = <TeContentProviderVariableDto>event.node.data;

            firstValueFrom(this.variableProviderService.expandSystemVariables(systemVariable.alias)).then(
                (res: MetaDataEntityDto) => {
                    this.nodeExpandOnMetaData(event.node, res);
                    this.busy = false;
                });

        } else if (event.node.data.objectTypeDtoId || event.node.data.joinObjectTypeId) {

            let id = event.node.data.objectTypeDtoId ? event.node.data.objectTypeDtoId : event.node.data.joinObjectTypeId;
            this.busy = true;

            firstValueFrom(this.metaService.getById(id, true, true, true, true, true)).then(
                (res: MetaDataEntityDto) => {

                    if (res.joins) {
                        res.joins.sort((a,b) => a.name.localeCompare(b.name));
                    }

                    this.nodeExpandOnMetaData(event.node, res);

                    this.busy = false;
                });

        } else if (event.node.data.typeId) {

            // assigning node attributes
            const children = event.node.data.attributes.map((element: any) => {
                // returns attribute object
                return this.setAttributes(element);
            });

            // assigning generic attributes to childrens in node
            event.node.children = children;

            this.busy = false;

        } else {

            // simple variables are leaf nodes, no meaningful children to show
            event.node.leaf = true;
            event.node.children = undefined;
            event.node.expanded = false;
            this.busy = false;
        }
    }

    /**
     * Expand a search-filtered node fully by loading complete metadata.
     * Preserves highlight flags from the search-pruned children.
     */
    expandNodeFully(node: TreeNode): void {
        const id = node.data?.objectTypeDtoId || node.data?.joinObjectTypeId;
        if (!id) return;

        // Collect matched identifiers from current search children
        const matchedAttrNames = new Set<string>();
        const matchedJoinNames = new Set<string>();
        if (node.children) {
            for (const child of node.children) {
                if (child.data?.matched) {
                    if (child.data.attributeName) {
                        matchedAttrNames.add(child.data.attributeName);
                    }
                    if (child.data.name) {
                        matchedJoinNames.add(child.data.name);
                    }
                }
                // Also check children of children (search may have nested matched joins)
                if (child.children) {
                    for (const grandchild of child.children) {
                        if (grandchild.data?.matched) {
                            if (grandchild.data.attributeName) matchedAttrNames.add(grandchild.data.attributeName);
                            if (grandchild.data.name) matchedJoinNames.add(grandchild.data.name);
                        }
                    }
                }
            }
        }

        this.busy = true;

        firstValueFrom(this.metaService.getById(id, true, true, true, true, true)).then(
            (res: MetaDataEntityDto) => {
                if (res.joins) {
                    res.joins.sort((a, b) => a.name.localeCompare(b.name));
                }

                // Build full children using existing method pattern
                const attributes = (res.attributes || []).map((element: any) => {
                    const attrNode: TreeNode = this.setAttributes(element);
                    // Transfer matched flag if this attribute was in the search results
                    if (matchedAttrNames.has(element.attributeName)) {
                        attrNode.data = { ...attrNode.data, matched: true };
                    }
                    attrNode.parent = node;
                    return attrNode;
                });

                const joins = (res.joins || []).map((item: any) => {
                    const joinNode = this.createNode(item, 'joins');
                    // Transfer matched flag if this join was in the search results
                    if (matchedJoinNames.has(item.name)) {
                        joinNode.data = { ...joinNode.data, matched: true };
                    }
                    joinNode.parent = node;
                    return joinNode;
                });

                let childrens: any[] = [];
                if (res.genericType && res.genericTypes) {
                    const genericType = res.genericTypes.map((item: any) => {
                        const gNode = this.createNode(item, 'generic');
                        gNode.parent = node;
                        return gNode;
                    });
                    childrens = childrens.concat(genericType, attributes, joins);
                } else {
                    childrens = childrens.concat(attributes, joins);
                }

                node.children = childrens;
                node.data.searchExpanded = false;
                node.styleClass = undefined;
                node.expanded = true;
                this.busy = false;
            }
        );
    }

    setAttributes(nodeItem: any) {
        // Function for setting attributes in nodes

        const dataType = DataTypeHelper.getInternalDataTypeForFormField(nodeItem);
        const icon = IconClassHelper.getIconClass(dataType);

        const attribute = {
            label: nodeItem.label,
            data: nodeItem,
            collapsedIcon: icon,
            leaf: true,
            type: 'attribute'
        };

        return attribute;
    }

    toggleNodeLabel() {
        this.displayTechnicalNames = !this.displayTechnicalNames;

        for (let node of this.variables) {
            // FIX: Same numeric/string variableType mismatch — cast to any for dual comparison
            const labelVarType: any = node.data.variableType;
            if (labelVarType !== 'object' && labelVarType !== TeVariableTypeEnum.object) {
                //it's an attribute so we can skip it
                continue;
            }

            this.updateNodeLabels(node);

        }
    }

    updateNodeLabels(node: TreeNode) {
        if (Object.getOwnPropertyNames(node).length === 0 || node.data.dataType) {
            return;
        }

        node.label = this.displayTechnicalNames ? node.data.name : node.data.label;

        if (!node.label) {
            // double check if we don't have label attribute, so we assign name
            node.label = node.data.name;
        }

        if (node.children) {

            for (let item of node.children) {
                this.updateNodeLabels(item);
            }

        }
    }

    /**
     * Handle search input with debounce.
     */
    onSearchInput(event: Event): void {
        const term = (event.target as HTMLInputElement).value.trim();
        if (!term) {
            this.searchResultTree = [];
            this.busy = false;
            return;
        }
        this.busy = true;
        this.searchSubject.next(term);
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.searchResultTree = [];
    }

    /**
     * Deep search: call backend recursive search endpoint and build a tree from the results.
     */
    async performDeepSearch(term: string): Promise<void> {
        if (!term || term.length < 2) {
            this.searchResultTree = [];
            return;
        }

        this.busy = true;
        try {
            const results = await firstValueFrom(
                this.templateVariableService.searchVariables(this.contentProviderId, term)
            );
            this.searchResultTree = this.buildSearchTree(results);
        } catch (e) {
            this.searchResultTree = [];
        }
        this.busy = false;
    }

    /**
     * Build a tree of TreeNode[] from backend search results.
     * Each VariableSearchResult becomes a root node.
     * The entities map is walked recursively to build the tree — reusing existing
     * createNode(), setAttributes() methods.
     */
    private buildSearchTree(results: any[]): TreeNode[] {
        const tree: TreeNode[] = [];

        for (const result of results) {
            // Create a root node similar to createRootNode
            const hasEntities = result.entities && result.variableObjectTypeId &&
                result.entities[result.variableObjectTypeId];

            const rootNode: TreeNode = {
                label: result.variableLabel || result.variableAlias,
                data: {
                    alias: result.variableAlias,
                    name: result.variableLabel,
                    variableType: hasEntities ? 'object' : 'simple',
                    objectTypeDtoId: result.variableObjectTypeId
                },
                expandedIcon: hasEntities ? 'fa-regular fa-cube text-blue-500' : 'fa-regular fa-font text-green-600',
                collapsedIcon: hasEntities ? 'fa-regular fa-cube text-blue-500' : 'fa-regular fa-font text-green-600',
                leaf: !hasEntities,
                expanded: true,
                type: hasEntities ? 'object-root' : 'simple-root',
                children: []
            };

            if (hasEntities) {
                // Build children from the entity map, track visited OTs to prevent cycles
                const rootEntity = result.entities[result.variableObjectTypeId];
                const visited = new Set<number>();
                visited.add(result.variableObjectTypeId);
                this.buildSearchChildren(rootNode, rootEntity, result.entities, visited);
            }

            tree.push(rootNode);
        }

        return tree;
    }

    /**
     * Recursively build tree children from a pruned entity and the flat entity map.
     * Returns true if any descendant (attribute or join) is matched.
     */
    private buildSearchChildren(parentNode: TreeNode, entity: any, entityMap: any, visited: Set<number>): boolean {
        const children: TreeNode[] = [];
        let hasMatchedDescendant = false;

        // Add matched attributes
        if (entity.attributes) {
            for (const attr of entity.attributes) {
                const attrNode: TreeNode = this.setAttributes(attr);
                attrNode.data = { ...attrNode.data, matched: attr.matched };
                attrNode.parent = parentNode;
                if (attr.matched) hasMatchedDescendant = true;
                children.push(attrNode);
            }
        }

        // Add matched joins
        if (entity.joins) {
            for (const join of entity.joins) {
                // Resolve label: prefer entityLabel from entity map over technical join label
                const joinEntityLabel = join.joinObjectTypeId && entityMap[join.joinObjectTypeId]
                    ? entityMap[join.joinObjectTypeId].entityLabel
                    : null;
                const resolvedJoin = { ...join };
                if (joinEntityLabel && (!join.label || join.label.includes('.'))) {
                    resolvedJoin.label = joinEntityLabel;
                }

                const joinNode: TreeNode = this.createNode(resolvedJoin, 'joins');
                joinNode.data = { ...joinNode.data, matched: join.matched };
                joinNode.parent = parentNode;
                if (join.matched) hasMatchedDescendant = true;

                // Recurse into the join's entity if it exists in the map and not yet visited
                if (join.joinObjectTypeId && entityMap[join.joinObjectTypeId] && !visited.has(join.joinObjectTypeId)) {
                    visited.add(join.joinObjectTypeId);
                    const childEntity = entityMap[join.joinObjectTypeId];
                    joinNode.leaf = false;
                    joinNode.children = [];
                    const childHasMatch = this.buildSearchChildren(joinNode, childEntity, entityMap, visited);
                    if (childHasMatch) hasMatchedDescendant = true;
                    joinNode.expanded = true;
                    joinNode.data.searchExpanded = true;
                    joinNode.styleClass = 'te-search-expanded';
                } else if (join.joinObjectTypeId) {
                    // Child entity not in search results or already visited — allow lazy expansion
                    joinNode.leaf = false;
                    joinNode.expanded = false;
                } else {
                    joinNode.leaf = true;
                }

                children.push(joinNode);
            }
        }

        parentNode.children = children;
        return hasMatchedDescendant;
    }

    nodeSelect(selectedNode: any) {
        this.onVariableSelect.emit(selectedNode);
    }

    /** Insert variable at editor cursor position */
    handleInsertVariable(node: TreeNode): void {
        const path = this.buildVariablePath(node);
        this.editorCommunicationService.insertVariable(path);
    }

    /** Open IF dialog for the given attribute with data-type-aware options */
    handleInsertIf(node: TreeNode): void {
        const path = this.buildVariablePath(node);
        const label = node.label || node.data?.label || node.data?.attributeName || path;
        const dataType = this.getSimpleDataType(node.data);
        const enumValues = this.getEnumValues(node.data);
        this.editorCommunicationService.openIfDialog(path, label, dataType, enumValues);
    }

    /** Insert a loop block for a collection node */
    handleInsertLoop(node: TreeNode): void {
        const path = this.buildVariablePath(node);
        const itemAlias = this.deriveItemAlias(node);
        this.editorCommunicationService.insertFreeMarker('<#list ' + path + ' as ' + itemAlias + '>\n\n</#list>');
    }

    /** Open table builder dialog for a collection node */
    handleInsertTable(node: TreeNode): void {
        const path = this.buildVariablePath(node);
        const itemAlias = this.deriveItemAlias(node);
        this.onTableInsert.emit({
            ...node.data,
            collectionPath: path,
            derivedItemAlias: itemAlias
        });
    }

    /**
     * Derive a clean FreeMarker loop variable alias from a collection node.
     * Uses the join name in clean string format (same as buildVariablePath).
     * Example: "cr.CustomerContract#customer" -> "cr_CustomerContract_attr_customer"
     */
    private deriveItemAlias(node: TreeNode): string {
        const name = node.data?.name || node.data?.alias || 'item';
        // Convert to clean string format (same as backend MetaDataJoin.convertJoinNameToCleanString)
        return name.replace(/#/g, '_attr_').replace(/\./g, '_');
    }

    /**
     * Map attribute metadata to a simple data type string used by IF dialog and copy popover.
     */
    private getSimpleDataType(nodeData: any): string {
        if (!nodeData) return 'string';

        // Check valueList flag, valueListEntries, or dataType string for enum detection
        if (nodeData.valueList
            || (nodeData.valueListEntries && nodeData.valueListEntries.length > 0)) {
            return 'enum';
        }

        const dt = (nodeData.dataType || '');
        const dtLower = dt.toLowerCase();

        // Enum types: Java enum class names contain "Enum" (e.g. "com.mvs.backend.cr.entity.CustomerStatusEnum")
        if (dt.includes('Enum') || dtLower.includes('enum')) return 'enum';

        if (dtLower.includes('boolean')) return 'boolean';
        if (dtLower.includes('date') || dtLower.includes('localdate') || dtLower.includes('localdatetime')) return 'date';
        if (dtLower.includes('bigdecimal')) return 'currency';
        if (dtLower.includes('integer') || dtLower.includes('long') || dtLower.includes('int')) return 'number';
        return 'string';
    }

    /**
     * Extract enum value labels from attribute metadata.
     * Checks valueListEntries first, then falls back to valueListDto entries.
     */
    private getEnumValues(nodeData: any): string[] {
        if (nodeData?.valueListEntries && nodeData.valueListEntries.length > 0) {
            return nodeData.valueListEntries.map((e: any) => e.label || e.technicalLabel);
        }
        // Fallback: check valueListDto which may hold the entries
        if (nodeData?.valueListDto?.entries && nodeData.valueListDto.entries.length > 0) {
            return nodeData.valueListDto.entries.map((e: any) => e.label || e.technicalLabel || e.value);
        }
        return [];
    }

    /** Open copy popover with context-aware options for the given node */
    handleCopyPopover(node: TreeNode, event: MouseEvent): void {
        event.stopPropagation();
        const path = this.buildVariablePath(node);
        const nodeType = node.type;
        const label = node.label || node.data?.label || node.data?.attributeName || path;

        let type: 'attribute' | 'collection' | 'join' = 'attribute';
        if (nodeType === 'collection') {
            type = 'collection';
        } else if (nodeType === 'join') {
            type = 'join';
        }

        const attrData: TeCopyAttrData = {
            path: path,
            label: label,
            type: type,
            dataType: this.getSimpleDataType(node.data),
            itemAlias: type === 'collection' ? this.deriveItemAlias(node) : undefined,
            values: this.getEnumValues(node.data)
        };

        if (this.copyPopoverRef) {
            this.copyPopoverRef.show(event, attrData);
        }
    }

    handleSimpleVariable(): void {
        this.variableToggle = true;
        this.visible = true;
    }

    handleObjectVariable(): void {
        this.variableToggle = false;
        this.visible = true;
        this.getAllObjectTypes(); // Lazy-load only when dialog opens
    }

    /**
     * Build full FreeMarker variable path from a tree node by walking up the parent chain.
     *
     * The backend (DataContext.resolveVariable) resolves joins by their MetaDataJoin.name
     * (e.g., "cr.CustomerContract#customer"). In FreeMarker paths, dots and # are not valid
     * so the backend uses a "clean string" conversion: '.' -> '_', '#' -> '_attr_'.
     * This makes "cr.CustomerContract#customer" -> "cr_CustomerContract_attr_customer".
     */
    private buildVariablePath(node: TreeNode): string {
        const parts: string[] = [];
        let current = node;

        while (current) {
            if (current.data) {
                if (current.data.attributeName && !current.data.joinRelationEnum) {
                    // Leaf attribute node — use the attribute name directly
                    let attrName = current.data.attributeName;
                    if (attrName.charAt(0) === '+') {
                        attrName = 'metaAttribute_' + attrName.substring(1);
                    }
                    parts.unshift(attrName);
                } else if (current.data.alias) {
                    // Root variable node — use the user-defined alias
                    parts.unshift(current.data.alias);
                } else if (current.data.name) {
                    // Join/collection node — convert join name to clean string format
                    // "cr.CustomerContract#customer" -> "cr_CustomerContract_attr_customer"
                    let name = current.data.name;
                    name = name.replace(/#/g, '_attr_').replace(/\./g, '_');
                    if (name.charAt(0) === '+') {
                        name = 'metaJoin_' + name.substring(1);
                    }
                    parts.unshift(name);
                }
            }
            current = current.parent;
        }

        return parts.join('.');
    }

    /**
     * Build a breadcrumb path segments array from a tree node by walking up the parent chain.
     * Returns empty array for root nodes (no parent path to show).
     */
    getNodeBreadcrumb(node: TreeNode): string[] {
        const parts: string[] = [];
        let current = node.parent;

        while (current) {
            const label = current.label || current.data?.label || current.data?.name || '';
            if (label) {
                parts.unshift(label);
            }
            current = current.parent;
        }

        return parts;
    }

    handleContextMenu(event: any, node: TreeNode) {
        // Only show context menu for root-level user variables (not system).
        // FIX: Same numeric/string variableType mismatch — cast to any for dual comparison.
        // System variables (id: 0, variableType: 2) must NOT show edit/delete context menu.
        const ctxVarType: any = node.data.variableType;
        if (!node.parent && ctxVarType !== 'system' && ctxVarType !== TeVariableTypeEnum.system) {
            this.selectedContextNode = node;
            this.contextMenuItems = [
                {
                    label: 'Bearbeiten',
                    icon: 'fa-regular fa-pen-to-square',
                    command: () => {
                        this.handleEditVariableName(node.data.id, node.data.alias);
                    }
                },
                {
                    label: 'Löschen',
                    icon: 'fa-regular fa-trash-can',
                    command: () => {
                        this.handleDeleteVariableName(node.data.id);
                    }
                }
            ];
        }
    }

    toggleDialog(toggle: boolean) {
        this.visible = true;
        this.variableToggle = toggle;
    }

    onCancel() {
        this.visible = false;
    }


    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes["id"]) {
            this.refreshComponent();
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.searchSubject.complete();
    }

    handleVariableDialogHide() {
        this.variableAlias = null;
        this.variableName = null;
        this.selectedVariableType = null;
    }
}
